if (!customElements.get('clearvero-announcement')) {
  customElements.define('clearvero-announcement', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.interval = Number(this.dataset.interval) || 10000;
      this.rotationEnabled = this.dataset.rotation === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pauseReasons = new Set();
      this.timer = null;
      this.section = this.closest('.shopify-section');
      this.onPrevious = () => this.show(this.index - 1, true);
      this.onNext = () => this.show(this.index + 1, true);
      this.onPointerEnter = (event) => {
        if (this.pauseOnHover && event.pointerType !== 'touch') this.pause('hover');
      };
      this.onPointerLeave = (event) => {
        if (this.pauseOnHover && event.pointerType !== 'touch') this.resume('hover');
      };
      this.onFocusIn = () => this.pause('focus');
      this.onFocusOut = (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.resume('focus');
      };
      this.onVisibility = () => {
        if (document.hidden) {
          this.pause('hidden');
        } else {
          this.resume('hidden');
        }
      };
      this.onReducedMotion = () => {
        if (this.reduceMotion.matches) {
          this.pause('reduced-motion');
        } else {
          this.resume('reduced-motion');
        }
      };
      this.onSectionSelect = (event) => {
        if (this.eventTargetsSection(event)) this.pause('editor');
      };
      this.onSectionDeselect = (event) => {
        if (this.eventTargetsSection(event)) this.resume('editor');
      };
      this.querySelector('[data-previous]')?.addEventListener('click', this.onPrevious);
      this.querySelector('[data-next]')?.addEventListener('click', this.onNext);
      if (this.pauseOnHover) {
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
      }
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.reduceMotion.addEventListener) {
        this.reduceMotion.addEventListener('change', this.onReducedMotion);
      } else {
        this.reduceMotion.addListener(this.onReducedMotion);
      }
      this.slides.forEach((slide, index) => {
        slide.removeAttribute('hidden');
        slide.toggleAttribute('inert', index !== this.index);
      });
      if (document.hidden) this.pauseReasons.add('hidden');
      if (this.reduceMotion.matches) this.pauseReasons.add('reduced-motion');
      if (this.pauseOnHover && this.matches(':hover')) this.pauseReasons.add('hover');
      if (this.contains(document.activeElement)) this.pauseReasons.add('focus');
      this.schedule();
    }

    disconnectedCallback() {
      this.clearTimer();
      this.querySelector('[data-previous]')?.removeEventListener('click', this.onPrevious);
      this.querySelector('[data-next]')?.removeEventListener('click', this.onNext);
      if (this.pauseOnHover) {
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
      }
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.reduceMotion?.removeEventListener) {
        this.reduceMotion.removeEventListener('change', this.onReducedMotion);
      } else {
        this.reduceMotion?.removeListener(this.onReducedMotion);
      }
      this.pauseReasons?.clear();
      this.initialized = false;
    }

    eventTargetsSection(event) {
      return event.target === this || event.target === this.section || event.target?.contains?.(this);
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.clearTimer();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.schedule();
    }

    clearTimer() {
      if (this.timer === null) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    canRotate() {
      return this.rotationEnabled
        && this.slides.length > 1
        && this.pauseReasons.size === 0
        && !document.hidden
        && !this.reduceMotion.matches;
    }

    schedule() {
      this.clearTimer();
      if (!this.canRotate()) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        if (!this.canRotate()) return;
        this.show(this.index + 1);
        this.schedule();
      }, this.interval);
    }

    show(nextIndex, restart = false) {
      if (!this.slides.length) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
      });
      if (restart) this.schedule();
    }
  });
}

if (!customElements.get('clearvero-header')) {
  customElements.define('clearvero-header', class extends HTMLElement {
    connectedCallback() {
      this.cartUpdateSequence = 0;
      this.lastAppliedCartUpdate = 0;
      this.cartUpdateResults = new Map();
      this.onCartUpdate = (event) => this.handleCartUpdate(event);
      this.onPageShow = (event) => {
        if (event.persisted) this.restoreConfirmedCartCount();
      };

      this.toggle = this.querySelector('[data-menu-toggle]');
      this.closeButton = this.querySelector('[data-menu-close]');
      this.menu = this.querySelector('[data-mobile-menu]');
      this.scroller = document.querySelector('.page-wrapper');
      this.onScroll = () => {
        const scrollPosition = Math.max(window.scrollY, this.scroller?.scrollTop || 0);
        this.classList.toggle('is-scrolled', scrollPosition > 16);
      };
      this.onResize = () => {
        this.measure();
        if (window.innerWidth >= 1100) this.closeMenu();
      };
      this.onToggle = () => this.openMenu();
      this.onClosePointerDown = (event) => {
        event.preventDefault();
        this.closeMenu(true);
      };
      this.onCloseKeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.closeMenu(true);
      };
      this.onClick = (event) => {
        if (event.target.closest('[data-menu-close]')) {
          event.preventDefault();
          this.closeMenu(true);
          return;
        }
        if (event.target.closest('[data-mobile-menu] a')) this.closeMenu();
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && !event.defaultPrevented && this.menu && !this.menu.hidden) this.closeMenu(true);
        if (event.key === 'Tab' && !this.menu.hidden) this.trapFocus(event);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller?.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      document.addEventListener('keydown', this.onKeydown);
      this.toggle?.addEventListener('click', this.onToggle);
      this.closeButton?.addEventListener('pointerdown', this.onClosePointerDown);
      this.closeButton?.addEventListener('keydown', this.onCloseKeydown);
      this.addEventListener('click', this.onClick);
      document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
      window.addEventListener('pageshow', this.onPageShow);
      this.onScroll();
      requestAnimationFrame(() => this.measure());
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.scroller?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.closeButton?.removeEventListener('pointerdown', this.onClosePointerDown);
      this.closeButton?.removeEventListener('keydown', this.onCloseKeydown);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    handleCartUpdate(event) {
      if (!event.promise || typeof event.promise.then !== 'function') return;

      const sequence = ++this.cartUpdateSequence;
      this.cartUpdateResults.set(sequence, { status: 'pending' });

      event.promise
        .then((result) => {
          this.cartUpdateResults.set(sequence, { status: 'fulfilled', result });
          this.applyLatestConfirmedCartUpdate();
        })
        .catch((error) => {
          this.cartUpdateResults.set(sequence, { status: 'rejected' });
          this.applyLatestConfirmedCartUpdate();

          if (error?.name !== 'AbortError') {
            console.warn('[clearvero-header] Cart event promise rejected:', error);
          }
        });
    }

    applyLatestConfirmedCartUpdate() {
      for (let sequence = this.cartUpdateSequence; sequence > this.lastAppliedCartUpdate; sequence--) {
        const update = this.cartUpdateResults.get(sequence);

        if (!update || update.status === 'pending') return;

        if (update.status === 'rejected') {
          this.cartUpdateResults.delete(sequence);
          continue;
        }

        const responseCount = update.result?.cart?.totalQuantity ?? update.result?.detail?.itemCount;
        const itemCount = Number(responseCount);
        if (!Number.isInteger(itemCount) || itemCount < 0) {
          this.cartUpdateResults.delete(sequence);
          continue;
        }

        this.lastAppliedCartUpdate = sequence;
        for (const storedSequence of this.cartUpdateResults.keys()) {
          if (storedSequence <= sequence) this.cartUpdateResults.delete(storedSequence);
        }

        this.renderCartCount(itemCount);
        return;
      }
    }

    renderCartCount(itemCount) {
      const cartLink = this.querySelector('.cv-header__cart');
      const cartCount = cartLink?.querySelector('.cv-header__cart-count');
      if (!cartLink || !cartCount) return;

      cartCount.textContent = String(itemCount);
      cartCount.classList.toggle('is-empty', itemCount === 0);
      cartLink.setAttribute('aria-label', (this.dataset.cartLabelTemplate || 'Cart, {count} items').split('{count}').join(String(itemCount)));

      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(itemCount),
          timestamp: Date.now(),
        })
      );
    }

    restoreConfirmedCartCount() {
      const storedCartCount = sessionStorage.getItem('cart-count');
      if (storedCartCount === null) return;

      try {
        const itemCount = Number(JSON.parse(storedCartCount).value);
        if (Number.isInteger(itemCount) && itemCount >= 0) {
          this.renderCartCount(itemCount);
        }
      } catch (_) {
        // Ignore invalid or unavailable cached state.
      }
    }

    measure() {
      const group = this.closest('#header-group');
      if (group) group.style.setProperty('--cv-header-overlay-height', `${group.offsetHeight}px`);
    }

    openMenu() {
      if (!this.menu) return;
      this.style.zIndex = '100';
      this.menu.hidden = false;
      this.toggle?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cv-menu-open');
      this.closeButton?.focus();
    }

    closeMenu(restoreFocus = false) {
      if (!this.menu) return;
      this.menu.hidden = true;
      this.style.removeProperty('z-index');
      this.toggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('cv-menu-open');
      if (restoreFocus) this.toggle?.focus();
    }

    trapFocus(event) {
      const focusable = [...this.menu.querySelectorAll('a[href], button:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
}
